<template>
  <div id="boligvelgeren">
    <div id="scrollHelper" />
    <transition
      v-if="loadStatus === 'loaded'"
      name="fade"
      mode="out-in"
    >
      <router-view />
    </transition>
    <transition
      v-else-if="loadStatus === 'empty' || loadStatus === 'error'"
      name="fade"
      mode="out-in"
    >
      <error-message />
    </transition>
    <transition
      v-else
      name="fade"
      mode="out-in"
    >
      <page-loader />
    </transition>
  </div>
</template>

<script>
// Vux ORM Models
import Availability from '@/models/Availability';
import Bathroom from '@/models/Bathroom';
import Bedroom from '@/models/Bedroom';
import Building from '@/models/Building';
import CalculatorSettings from '@/models/CalculatorSettings';
import Facility from '@/models/Facility';
import Level from '@/models/Level';
import Image from '@/models/Image';
import Illustration from '@/models/Illustration';
import Option from '@/models/Option';
import OptionCategory from '@/models/OptionCategory';
import Project from '@/models/Project';
import Residence from '@/models/Residence';
import ResidenceType from '@/models/ResidenceType';
import Selector from '@/models/Selector';
import Setting from '@/models/Setting';

// Components
import ErrorMessage from '@/components/ErrorMessage.vue';
import PageLoader from '@/components/PageLoader.vue';

export default {
  components: {
    ErrorMessage,
    PageLoader,
  },
  computed: {
    loadStatus() {
      return this.$store.state.entities.residences.loadStatus;
    },
  },
  created() {
    this.setup();

    // Check if there is something in ?project= in URL and if so set in store
    if (this.$route.query.project) {
      this.updateProject(this.$route.query.project);
    }

    // Check if there is something in ?level= in URL and if so set in store
    if (this.$route.query.level) {
      this.updateLevel(this.$route.query.level);
    }

    if (this.$route.query.building) {
      this.updateBuilding(this.$route.query.building);
    }

    if (this.$route.query.availability) {
      this.updateAvailability(this.$route.query.availability);
    }

    if (this.$route.query.minPrice) {
      this.updateMinPrice(this.$route.query.minPrice);
    }

    if (this.$route.query.maxPrice) {
      this.updateMaxPrice(this.$route.query.maxPrice);
    }

    if (this.$route.query.minSize) {
      this.updateMinSize(this.$route.query.minSize);
    }

    if (this.$route.query.maxSize) {
      this.updateMaxSize(this.$route.query.maxSize);
    }

    if (this.$route.query.order) {
      this.updateOrder(this.$route.query.order);
    }

    if (this.$route.query.bedrooms) {
      this.updateBedrooms(this.$route.query.bedrooms);
    }

    if (this.$route.query.bathrooms) {
      this.updateBathrooms(this.$route.query.bathrooms);
    }

    if (this.$route.query.residenceType) {
      this.updateResidenceType(this.$route.query.residenceType);
    }
    

  },
  methods: {
    setup() {
      const settings = window.__selector_settings__;
      if (settings) {
        const projectId = settings.sourceData.project;
        const baseURL = settings.sourceData.sourceURL;
        const hiddenId = settings.sourceData.hiddenId;

        // Import data
        this.getTaxonomies(projectId, baseURL);

        this.getIllustrations(projectId, baseURL);

        this.getResidences(projectId, baseURL, hiddenId);
        this.getSelectors(projectId, baseURL);

        // View settings
        Setting.commit(state => {
          state.colors = settings.viewSettings.colors;
        });

        // Check if listType is not false and set predefined choice in state.
        if (settings.viewSettings.listType !== false) {
          Residence.commit(state => {
            state.listType = settings.viewSettings.listType;
          });
        }

        // Check if there is a set image size for card view thumbnails.
        if (settings.viewSettings.cardImageSize !== false) {
          Image.commit(state => {
            state.cardImageSize = settings.viewSettings.cardImageSize;
          });
        }
        
        // Check if there should be options and if so fetch data
        if (settings.options.hasOptions === true) {
          this.getOptionCategories(baseURL);
          this.getOptions(projectId, baseURL);
          Option.commit(state => {
            state.hasOptions = true;
          });
        }

        if (settings.calculator.hasCalculator === true) {
          CalculatorSettings.create({
            data: settings.calculator,
          });
          CalculatorSettings.commit(state => {
            state.hasCalculator = true;
          });
          if (settings.calculator.pricingSelect === true) { // TODO: Deprecated
            CalculatorSettings.commit(state => {
              state.hasCommunalPrice = true;
            });
          }
          if (settings.calculator.interestRateIndividualSelect === true) {
            CalculatorSettings.commit(state => {
              state.hasMortgage = true;
            });
          }
          if (settings.calculator.taxDeductionCheck === true) {
            CalculatorSettings.commit(state => {
              state.hasTaxDeduction = true;
            });
          }
          if (localStorage.getItem('equity')) {
            CalculatorSettings.commit(state => {
              state.equity = parseFloat(localStorage.getItem('equity'));
            });
          }
          if (localStorage.getItem('mortgageInterestRate')) {
            CalculatorSettings.commit(state => {
              state.mortgageInterestRate = parseFloat(localStorage.getItem('mortgageInterestRate'));
            });
          }
          if (localStorage.getItem('mortgageYears')) {
            CalculatorSettings.commit(state => {
              state.mortgageYears = parseFloat(localStorage.getItem('mortgageYears'));
            });
          }
        }

        if (settings.contactForm.hasContactForm === true) {
          Residence.commit(state => {
            state.hasContactForm = true;
          });
        }

        if (settings.viewSettings.colorBuilding === true) {
          Residence.commit(state => {
            state.colorBuilding = true;
          });
        }

        if (settings.viewSettings.colorResidence === true) {
          Residence.commit(state => {
            state.colorResidence = true;
          });
        }

        if (settings.viewSettings.reservedPrice === true) {
          Residence.commit(state => {
            state.reservedPrice = true;
          });
        }
        
        if (settings.viewSettings.soldPrice === true) {
          Residence.commit(state => {
            state.soldPrice = true;
          });
        }

      } else { //TODO: Deprecated?
        this.getIllustrations();
        this.getOptionCategories();
        this.getResidences();
        this.getSelectors();
        this.getOptions();
      }
    },
    getTaxonomies(projectId, baseURL) {
      Availability.fetch(baseURL);
      Building.fetch(baseURL);
      Bathroom.fetch(baseURL);
      Bedroom.fetch(baseURL);
      Facility.fetch(baseURL);
      Level.fetch(baseURL);
      Project.fetch(baseURL, projectId);
      ResidenceType.fetch(baseURL);
    },
    getResidences(projectId, baseURL, exclude) {
      Residence.fetch(projectId, baseURL, exclude);
    },
    getSelectors(projectId, baseURL) {
      Selector.fetch(projectId, baseURL);
    },
    getOptions(projectId, baseURL) {
      Option.fetch(projectId, baseURL);
    },
    getOptionCategories(baseURL) {
      OptionCategory.fetch(baseURL);
    },
    getIllustrations(projectId, baseURL) {
      Illustration.fetch(projectId, baseURL);
    },

    /* ======================================
     * Update State
     * ====================================== */
    updateProject(project){
      Residence.commit(state => {
        state.selectedProject = project;
      });
    },
    updateLevel(level){
      Residence.commit(state => {
        state.selectedLevel = level;
      });
    },
    updateBuilding(building){
      Building.commit(state => {
        state.selectedBuilding = building;
      });
    },
    updateAvailability(availability){
      Residence.commit(state => {
        state.residenceStatus = availability;
      });
    },
    updateMinPrice(minPrice){
      Residence.commit(state => {
        state.minPrice = parseInt(minPrice);
      });
    },
    updateMaxPrice(maxPrice){
      Residence.commit(state => {
        state.maxPrice = parseInt(maxPrice);
      });
    },
    updateMinSize(minSize){
      Residence.commit(state => {
        state.minSize = parseInt(minSize);
      });
    },
    updateMaxSize(maxSize) {
      Residence.commit(state => {
        state.maxSize = parseInt(maxSize);
      });
    },
    updateOrder(order){
      Residence.commit(state => {
        state.order = order;
      });
    },
    updateBedrooms(bedrooms){
      Residence.commit(state => {
        state.bedrooms = bedrooms;
      });
    },
    updateBathrooms(bathrooms){
      Residence.commit(state => {
        state.bathrooms = bathrooms;
      });
    },
    updateResidenceType(residenceType){
      Residence.commit(state => {
        state.selectedResidenceType = residenceType;
      });
    },

  },
};
</script>
